import React, { Component } from "react";
import { withStyles } from "@material-ui/core/styles";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { batch } from "react-redux";
import produce from "immer";

import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { bmodeOptions } from "../../constants/highcharts/bmodeHeatmapOptions";

// Material UI
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Grid from "@material-ui/core/Grid";
import Switch from "@material-ui/core/Switch";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import Slider from "@material-ui/core/Slider";
import Accordion from "@material-ui/core/Accordion";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import ListSubheader from "@material-ui/core/ListSubheader";
import Divider from "@material-ui/core/Divider";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";

import { colors } from "../../constants/colors";
import { minSliceThickness } from "../../constants/constants";
import { customTheme } from "../../constants/customTheme";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import WarningIcon from "@material-ui/icons/Warning";

// Actions
import {
  updateLocationWideRoiReference,
  setBmodeThickness,
  calculateThickness,
  updateDefaultRoiReference,
  setDefaultRoiReference,
  setLocationWideRoiReference,
  fetchBmodePlot,
  changeGlobalBmodeColorbar,
  toggleBmodeSetToAutoDetectedThickness,
} from "../../actions";
import { studyFixture } from "../../constants/Fixtures/studyFixture";
const styles = {
  formLabel: {
    marginLeft: 0,
    marginRight: 0,
    "& .MuiTypography-root": {
      fontSize: "1rem",
    },
  },
  graphTitleContainer: {
    textAlign: "center",
  },
  graphTitle: {
    fontSize: "1.2rem",
  },
  graphControlsContainer: {
    padding: "20px",
  },
  alertGrid: {
    padding: "0px 20px 0px 20px",
  },
  divider: {
    width: "100%",
    marginTop: "10px",
    backgroundColor: colors.disabledColor,
  },
  graphSettingsSubheader: {
    height: "35px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.875rem",
    fontWeight: 400,
  },
  buttonSecondary: (props) => customTheme(props.theme).buttonSecondary,
  buttonOutlinedSecondary: (props) =>
    customTheme(props.theme).buttonOutlinedSecondary,
  colorbarButtons: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-evenly",
  },
  graphSettingsGrid: {
    display: "flex",
    justifyContent: "center",
  },
  graphSettingsSubGrid: {
    padding: "20px",
    margin: "20px 0px 40px 0px",
    "& div": {
      flex: "1 1 auto",
    },
  },
  sliderDiv: {
    marginBottom: "10px",
    justifyContent: "space-between",
  },
  sliderContainer: {
    margin: "20px 20px 40px 20px",
    width: "70%",
  },
  sliderTitle: {
    marginBottom: "20px",
  },
  listIcon: {
    ...customTheme().iconMedium,
    color: (props) => colors.warning,
  },
  listText: {
    "& span": {
      fontSize: "1rem",
    },
    "& p": {
      fontSize: "1rem",
    },
  },
};

class BmodeHeatmap extends Component {
  constructor(props) {
    super(props);
    this.updateWindowDimensions = this.updateWindowDimensions.bind(this);
    this.state = {
      startDragY: null, // keeps track of the mouse y position when a drag is initiated
      lineStartY: null, // keeps track of initial y position of element when a drag is initiated
      bmodeColorBarRange: [
        Math.round(this.props.colorBar.range[0]),
        Math.round(this.props.colorBar.range[1]),
      ],
      expandGraphSettings: false,
    };
  }
  updateWindowDimensions() {
    let newWidth = window.innerWidth * 0.6 - 20;
    let newHeight = newWidth * 0.7;
    this.setState({
      ...this.state,
      windowHeight: newHeight,
      windowWidth: newWidth, // The result column is always 60% of the window. Based on window size calculate the graph size and always leave 20px gap on each side
      colorBarHeight: newHeight - 64, // 64 px is the height of the x-axis labels
    });
  }

  componentDidMount() {
    // this.initializeEventListeners();
    this.updateWindowDimensions();
    window.addEventListener("resize", this.updateWindowDimensions);
  }

  componentDidUpdate(prevProps) {
    // this.initializeEventListeners();
    // Update the thickness value based on "keepAutoDetectThickness" toggle when the bmode limits are changed
    if (prevProps.sample.id !== this.props.sample.id) {
      this.setState({
        ...this.state,
        bmodeColorBarRange: [
          Math.round(this.props.colorBar.range[0]),
          Math.round(this.props.colorBar.range[1]),
        ],
      });
    }
  }

  initializeEventListeners = () => {
    const plotLineTop = document.getElementsByClassName("skinRegionStart")[0];
    const plotLineBottom = document.getElementsByClassName("skinRegionEnd")[0];
    if (
      plotLineTop &&
      plotLineBottom &&
      !this.props.sample.processing &&
      !this.props.locked &&
      !this.props.switchDisabled
    ) {
      // add event listener for plotLines
      // Mouse Events
      plotLineTop.addEventListener("mousedown", this.handleTopDown);
      plotLineBottom.addEventListener("mousedown", this.handleBottomDown);
      // Touch Events
      plotLineTop.addEventListener("touchstart", this.handleTopDownTouch);
      plotLineBottom.addEventListener("touchstart", this.handleBottomDownTouch);
    }
  };

  componentWillUnmount() {
    window.removeEventListener("resize", this.updateWindowDimensions);
    // clean up/remove event listeners
    const plotLineTop = document.getElementsByClassName("skinRegionStart")[0];
    const plotLineBottom = document.getElementsByClassName("skinRegionEnd")[0];
    if (plotLineTop && plotLineBottom) {
      // Mouse Events
      plotLineTop.removeEventListener("mousedown", this.handleTopDown);
      plotLineBottom.removeEventListener("mousedown", this.handleBottomDown);

      // Touch Events
      plotLineTop.removeEventListener("touchend", this.handleTopDownTouch);
      plotLineBottom.removeEventListener(
        "touchend",
        this.handleBottomDownTouch
      );
    }
  }

  handleTopDown = (e) => {
    document.addEventListener("mousemove", this.handleTopDrag);
    document.addEventListener("mouseup", this.handleTopRelease);

    this.setState({
      startDragY: e.clientY, // save initial y value for startDragY
      lineStartY: e.target.getBoundingClientRect().y,
    });
  };

  handleBottomDown = (e) => {
    document.addEventListener("mousemove", this.handleBottomDrag);
    document.addEventListener("mouseup", this.handleBottomRelease);

    this.setState({
      startDragY: e.clientY, // save initial y value for startDragY
      lineStartY: e.target.getBoundingClientRect().y,
    });
  };

  // Touch Events
  handleTopDownTouch = (e) => {
    // e.preventDefault(); // prevent default scrolling and clicking action
    e.stopPropagation();
    e.stopImmediatePropagation();
    document
      .getElementsByClassName("skinRegionStart")[0]
      .addEventListener("touchmove", this.handleTopDragTouch);
    document
      .getElementsByClassName("skinRegionStart")[0]
      .addEventListener("touchend", this.handleTopReleaseTouch);
    this.setState({
      startDragY: e.changedTouches[0].clientY, // save initial y value for startDragY
      lineStartY: e.target.getBoundingClientRect().y,
    });
  };

  handleBottomDownTouch = (e) => {
    // e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    document
      .getElementsByClassName("skinRegionEnd")[0]
      .addEventListener("touchmove", this.handleBottomDragTouch);
    document
      .getElementsByClassName("skinRegionEnd")[0]
      .addEventListener("touchend", this.handleBottomReleaseTouch);

    this.setState({
      startDragY: e.changedTouches[0].clientY, // save initial y value for startDragY
      lineStartY: e.target.getBoundingClientRect().y,
    });
  };

  handleTopDrag = (e) => {
    const plotLineTop = document.getElementsByClassName("skinLineStart")[0];
    const regionTop = document.getElementsByClassName("skinRegionStart")[0];
    const plotLineBottom = document.getElementsByClassName("skinLineEnd")[0];

    const plotBkg = document
      .getElementById("bmodeHeatmap")
      .getElementsByClassName("highcharts-plot-background")[0];

    const topBoundary = plotBkg.getBoundingClientRect().top;
    const bottomLineY = plotLineBottom.getBoundingClientRect().y;
    const dMouseY = e.clientY - this.state.startDragY; // distance mouse has moved

    const newLineTopY = this.state.lineStartY + dMouseY;
    const newThickness = this.convertToMm(bottomLineY - newLineTopY);

    if (
      newLineTopY < bottomLineY && // above bottom of chart
      newLineTopY > topBoundary && // below top line
      newThickness >= minSliceThickness // thickness is above minimum
    ) {
      plotLineTop.setAttribute("transform", `translate(0, ${dMouseY})`);
      regionTop.setAttribute("transform", `translate(0, ${dMouseY})`);
    }
  };

  handleBottomDrag = (e) => {
    const plotLineTop = document.getElementsByClassName("skinLineStart")[0];
    const plotLineBottom = document.getElementsByClassName("skinLineEnd")[0];
    const regionBottom = document.getElementsByClassName("skinRegionEnd")[0];

    const plotBkg = document
      .getElementById("bmodeHeatmap")
      .getElementsByClassName("highcharts-plot-background")[0];

    const topLineY = plotLineTop.getBoundingClientRect().y;
    const bottomBoundary = plotBkg.getBoundingClientRect().bottom;
    const dMouseY = e.clientY - this.state.startDragY; // distance mouse has moved
    const newLineBottomY = this.state.lineStartY + dMouseY;
    const newThickness = this.convertToMm(newLineBottomY - topLineY);

    if (
      newLineBottomY < bottomBoundary && // above chart bottom
      newLineBottomY > topLineY && // below top line
      newThickness >= minSliceThickness // thickness is above minimum
    ) {
      plotLineBottom.setAttribute("transform", `translate(0, ${dMouseY})`);
      regionBottom.setAttribute("transform", `translate(0, ${dMouseY})`);
    }
  };

  handleTopDragTouch = (e) => {
    e.preventDefault();
    const plotLineTop = document.getElementsByClassName("skinLineStart")[0];
    const regionTop = document.getElementsByClassName("skinRegionStart")[0];
    const plotLineBottom = document.getElementsByClassName("skinLineEnd")[0];

    const plotBkg = document
      .getElementById("bmodeHeatmap")
      .getElementsByClassName("highcharts-plot-background")[0];

    const topBoundary = plotBkg.getBoundingClientRect().top;
    const bottomLineY = plotLineBottom.getBoundingClientRect().y;
    const dMouseY = e.changedTouches[0].clientY - this.state.startDragY; // distance mouse has moved

    const newLineTopY = this.state.lineStartY + dMouseY;
    const newThickness = this.convertToMm(bottomLineY - newLineTopY);

    if (
      newLineTopY < bottomLineY && // above bottom of chart
      newLineTopY > topBoundary && // below top line
      newThickness >= minSliceThickness // thickness is above minimum
    ) {
      plotLineTop.setAttribute("transform", `translate(0, ${dMouseY})`);
      regionTop.setAttribute("transform", `translate(0, ${dMouseY})`);
    }
  };

  handleBottomDragTouch = (e) => {
    e.preventDefault();
    const plotLineTop = document.getElementsByClassName("skinLineStart")[0];
    const plotLineBottom = document.getElementsByClassName("skinLineEnd")[0];
    const regionBottom = document.getElementsByClassName("skinRegionEnd")[0];

    const plotBkg = document
      .getElementById("bmodeHeatmap")
      .getElementsByClassName("highcharts-plot-background")[0];

    const topLineY = plotLineTop.getBoundingClientRect().y;
    const bottomBoundary = plotBkg.getBoundingClientRect().bottom;
    const dMouseY = e.changedTouches[0].clientY - this.state.startDragY; // distance mouse has moved
    const newLineBottomY = this.state.lineStartY + dMouseY;
    const newThickness = this.convertToMm(newLineBottomY - topLineY);

    if (
      newLineBottomY < bottomBoundary && // above chart bottom
      newLineBottomY > topLineY && // below top line
      newThickness >= minSliceThickness // thickness is above minimum
    ) {
      plotLineBottom.setAttribute("transform", `translate(0, ${dMouseY})`);
      regionBottom.setAttribute("transform", `translate(0, ${dMouseY})`);
    }
  };

  handleTopRelease = () => {
    document.removeEventListener("mousemove", this.handleTopDrag);
    document.removeEventListener("mouseup", this.handleTopRelease);

    const newTop = this.calculateYMm(
      document.getElementsByClassName("skinLineStart")[0]
    );
    this.changeSampleThickness(newTop, this.props.bottomSurface);
  };

  handleBottomRelease = () => {
    document.removeEventListener("mousemove", this.handleBottomDrag);
    document.removeEventListener("mouseup", this.handleBottomRelease);

    const newBottom = this.calculateYMm(
      document.getElementsByClassName("skinLineEnd")[0]
    );
    this.changeSampleThickness(this.props.topSurface, newBottom);
  };

  handleTopReleaseTouch = () => {
    document
      .getElementsByClassName("skinRegionStart")[0]
      .removeEventListener("touchmove", this.handleTopDrag);
    document
      .getElementsByClassName("skinRegionStart")[0]
      .removeEventListener("touchend", this.handleTopRelease);

    const newTop = this.calculateYMm(
      document.getElementsByClassName("skinLineStart")[0]
    );
    this.changeSampleThickness(newTop, this.props.bottomSurface);
  };

  handleBottomReleaseTouch = () => {
    document
      .getElementsByClassName("skinRegionEnd")[0]
      .removeEventListener("touchmove", this.handleBottomDrag);
    document
      .getElementsByClassName("skinRegionEnd")[0]
      .removeEventListener("touchend", this.handleBottomRelease);

    const newBottom = this.calculateYMm(
      document.getElementsByClassName("skinLineEnd")[0]
    );
    this.changeSampleThickness(this.props.topSurface, newBottom);
  };

  //////////////////////
  // HELPER FUNCTIONS //
  //////////////////////

  // converts from pixel (DOM) units to highcharts units
  convertToMm = (value) => {
    const plotBkgHeight = document
      .getElementById("bmodeHeatmap")
      .getElementsByClassName("highcharts-plot-background")[0]
      .getBoundingClientRect().height;

    const chartRangeY = this.props.yAxis.range[1] - this.props.yAxis.range[0];
    return this.props.yAxis.range[0] + (value * chartRangeY) / plotBkgHeight;
  };

  calculateYMm = (element) => {
    const plotBkgTop = document
      .getElementById("bmodeHeatmap")
      .getElementsByClassName("highcharts-plot-background")[0]
      .getBoundingClientRect().top;

    const elementY = element.getBoundingClientRect().y;
    const elementYMm = this.convertToMm(elementY - plotBkgTop);
    return elementYMm;
  };

  /////////////////
  // OTHER STUFF //
  /////////////////

  changeSampleThickness = (top, bottom) => {
    batch(() => {
      this.props.setBmodeThickness(top, bottom, this.props.sample.id);
      this.props.calculateThickness(this.props.sample.id); // calculate the new average thickness for the location
    });
    if (this.props.roiReference.locationWide.id === this.props.sample.id) {
      this.props.updateLocationWideRoiReference(null, this.props.locationId);
    } else if (this.props.roiReference.default === this.props.sample.id) {
      this.props.updateDefaultRoiReference(
        this.props.sample.id,
        this.props.locationId,
        this.props.sample.zValue,
        this.props.sample.thickness
      );
    }

    if (this.props.setToAutoDetectedThickness) {
      this.props.toggleBmodeSetToAutoDetectedThickness(this.props.sample.id);
    }
  };

  handleToggleAutoDetectedThickness = (event) => {
    if (event.target.checked) {
      this.props.setBmodeThickness(
        this.props.autoTopSurface,
        this.props.autoBottomSurface,
        this.props.sample.id
      );

      if (this.props.roiReference.locationWide.id === this.props.sample.id) {
        this.props.updateLocationWideRoiReference(null, this.props.locationId);
      } else if (this.props.roiReference.default === this.props.sample.id) {
        this.props.updateDefaultRoiReference(
          this.props.sample.id,
          this.props.locationId,
          Number(
            (
              (this.props.autoTopSurface + this.props.autoBottomSurface) /
              2
            ).toFixed(5)
          ),
          Math.abs(this.props.autoBottomSurface - this.props.autoTopSurface) // in mm
        );
      }
      //calculate the new average thickness for the location
      this.props.calculateThickness(this.props.sample.id);
    }

    this.props.toggleBmodeSetToAutoDetectedThickness(this.props.sample.id);
  };

  handleLocationWideRoiToggleChange = (event) => {
    if (event.target.checked && this.props.sample.thickness >= 0.0001) {
      this.props.updateLocationWideRoiReference(
        this.props.sample.id,
        this.props.locationId
      );
    } else {
      this.props.updateLocationWideRoiReference(null, this.props.locationId);
    }
  };

  handleDefaultRoiToggleChange = (event) => {
    if (event.target.checked && this.props.sample.thickness >= 0.0001) {
      this.props.updateDefaultRoiReference(
        this.props.sample.id,
        this.props.locationId,
        this.props.sample.zValue,
        this.props.sample.thickness
      );
    } else {
      this.props.updateDefaultRoiReference(
        null,
        this.props.locationId,
        null,
        null
      );
    }
  };

  ////////////////////
  // HANDLE SLIDERS //
  ////////////////////

  handleBmodeColorBarChange = (e, newValue) => {
    if (newValue !== this.state.bmodeColorBarRange) {
      this.setState({
        bmodeColorBarRange: newValue,
      });
    }
  };

  handleCancelBmodeColorBarChange = (e) => {
    this.setState({
      bmodeColorBarRange: [
        Math.round(this.props.colorBar.range[0]),
        Math.round(this.props.colorBar.range[1]),
      ],
    });
  };

  commitBmodeColorBarChange = () => {
    if (
      this.state.bmodeColorBarRange !==
      [
        Math.round(this.props.colorBar.range[0]),
        Math.round(this.props.colorBar.range[1]),
      ]
    ) {
      this.props.fetchBmodePlot(
        this.props.sample.id,
        this.state.bmodeColorBarRange
      );
    }
  };

  commitGlobalBmodeColorBarChange = () => {
    this.props.changeGlobalBmodeColorbar(this.state.bmodeColorBarRange);
  };

  render() {
    const { classes } = this.props;
    // Update chart with values from redux store
    const chartOptions = produce(
      { ...bmodeOptions(this.props.theme) },
      (draft) => {
        const sample = this.props.sample;
        // Chart
        draft.chart.plotBackgroundImage = this.props.backgroundImage;
        draft.chart.height = this.state.windowHeight; // resize chart based on screen size
        draft.chart.width = this.state.windowWidth; // resize chart based on screen size

        // yAxis
        draft.yAxis.min = this.props.yAxis.range[0];
        draft.yAxis.max = this.props.yAxis.range[1];
        draft.yAxis.labels.formatter = function () {
          let newValue = this.value;
          if (sample.calib !== null) {
            newValue -= sample.calib;
          }
          return `${newValue.toFixed(1)}`;
        };

        draft.yAxis.title.text = `${this.props.yAxis.label} (${this.props.yAxis.unit})`;
        // plotLines
        draft.yAxis.plotLines[0].value = this.props.topSurface; // line
        draft.yAxis.plotLines[1].value = this.props.topSurface; // grabbable region
        draft.yAxis.plotLines[2].value = this.props.bottomSurface; // line
        draft.yAxis.plotLines[3].value = this.props.bottomSurface; // grabbable region

        // xAxis
        draft.xAxis.min = this.props.xAxis.range[0];
        draft.xAxis.max = this.props.xAxis.range[1];
        draft.xAxis.title.text = `${this.props.xAxis.label} (${this.props.xAxis.unit})`;

        // colorAxis
        draft.legend.symbolHeight = this.state.colorBarHeight;
        draft.colorAxis.min = -1 * this.props.colorBar.range[1];
        draft.colorAxis.max = -1 * this.props.colorBar.range[0];
      }
    );

    return (
      <React.Fragment>
        {/* HEATMAP */}
        <Grid item className={classes.graphTitleContainer}>
          {/* TITLE*/}
          <Typography className={classes.graphTitle}>
            {`${this.props.acqTypes["BMODE"]["name"]} Sample ${this.props.sampleCount}/${this.props.totalSamples}`}
          </Typography>
        </Grid>
        <Grid item container justify="center" id="bmodeHeatmap">
          <HighchartsReact highcharts={Highcharts} options={chartOptions} />
        </Grid>
        {this.props.sample.alert.state && (
          <Grid item container wrap="nowrap" className={classes.alertGrid}>
            <ListItem>
              <ListItemIcon>
                <WarningIcon className={classes.listIcon} />
              </ListItemIcon>
              <ListItemText
                className={classes.listText}
                primary={this.props.sample.alert.message}
              />
            </ListItem>
          </Grid>
        )}
        <Grid
          item
          container
          wrap="nowrap"
          className={classes.graphControlsContainer}
        >
          <Accordion
            className={classes.accordian}
            expanded={this.state.expandGraphSettings}
            onChange={() =>
              this.setState({
                expandGraphSettings: !this.state.expandGraphSettings,
              })
            }
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>Image Settings</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid
                item
                container
                direction="row"
                className={classes.graphSettingsGrid}
              >
                <Divider className={classes.divider} />
                <ListSubheader
                  disableSticky
                  className={classes.graphSettingsSubheader}
                >
                  Thickness Settings
                </ListSubheader>
                {/* Controls */}
                <Grid
                  item
                  container
                  wrap="nowrap"
                  direction="column"
                  className={classes.graphSettingsSubGrid}
                >
                  {/* AUTO_DETECT */}
                  <FormControlLabel
                    key={"autoDetectToggle"}
                    className={classes.formLabel}
                    control={
                      <Switch
                        checked={this.props.setToAutoDetectedThickness}
                        disabled={
                          true
                          //  && this.props.switchDisabled
                        }
                        onChange={(e) =>
                          this.handleToggleAutoDetectedThickness(e)
                        }
                        color="secondary"
                      />
                    }
                    label={`Set to auto-detect thickness`}
                  />

                  {/* ROI FOR ALL SWITCH */}
                  <FormControlLabel
                    key={"RoiToggle"}
                    className={classes.formLabel}
                    control={
                      <Switch
                        checked={
                          this.props.roiReference.locationWide.id ===
                          this.props.sample.id
                        }
                        disabled={
                          true
                          // this.props.switchDisabled) ||
                          // !this.props.samplesDoneFlag
                        }
                        onChange={(e) =>
                          this.handleLocationWideRoiToggleChange(e)
                        }
                        color="secondary"
                      />
                    }
                    label={`Apply skin thickness to all ${this.props.acqTypes.CSI.name} samples`}
                  />

                  {/* DEFAULT ROI SWITCH */}
                  <FormControlLabel
                    key={"DefaultRoiToggle"}
                    className={classes.formLabel}
                    control={
                      <Switch
                        checked={
                          this.props.roiReference.default ===
                            this.props.sample.id ||
                          this.props.roiReference.locationWide.id ===
                            this.props.sample.id
                        }
                        disabled={
                          true
                          // ( this.props.switchDisabled) ||
                          // this.props.roiReference.locationWide.id !== null
                        }
                        onChange={(e) => this.handleDefaultRoiToggleChange(e)}
                        color="secondary"
                      />
                    }
                    label="Set as default ROI reference"
                  />
                </Grid>

                <Divider className={classes.divider} />
                <ListSubheader
                  disableSticky
                  className={classes.graphSettingsSubheader}
                >
                  Colorbar Settings
                </ListSubheader>
                <Grid
                  item
                  container
                  direction="column"
                  className={classes.graphSettingsSubGrid}
                >
                  {/* COLORBARS */}
                  <Grid
                    item
                    container
                    alignItems="center"
                    className={classes.sliderDiv}
                  >
                    <Typography className={classes.sliderTitle}>
                      {this.props.colorBar.label}:
                    </Typography>
                    <Slider
                      value={this.state.bmodeColorBarRange}
                      step={10}
                      className={classes.sliderContainer}
                      color={"secondary"}
                      aria-labelledby="discrete-slider-always"
                      min={-60}
                      max={0}
                      valueLabelFormat={(value) =>
                        `${value}${this.props.colorBar.unit}`
                      }
                      marks={[
                        {
                          value: -60,
                          label: `-60 ${this.props.colorBar.unit}`,
                        },
                        {
                          value: -50,
                        },
                        {
                          value: -40,
                        },
                        {
                          value: -30,
                          label: `-30 ${this.props.colorBar.unit}`,
                        },
                        {
                          value: -20,
                        },
                        {
                          value: -10,
                        },
                        {
                          value: 0,
                          label: `0 ${this.props.colorBar.unit}`,
                        },
                      ]}
                      valueLabelDisplay="on"
                      disabled={this.props.switchDisabled}
                      onChange={this.handleBmodeColorBarChange}
                    />
                  </Grid>
                  <Grid item container className={classes.colorbarButtons}>
                    <Button
                      onClick={this.handleCancelBmodeColorBarChange}
                      disabled={this.props.switchDisabled}
                      className={classes.buttonOutlinedSecondary}
                      variant="outlined"
                      color="secondary"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={this.commitBmodeColorBarChange}
                      disabled={
                        this.props.switchDisabled ||
                        (this.state.bmodeColorBarRange[0] ===
                          Math.round(this.props.colorBar.range[0]) &&
                          this.state.bmodeColorBarRange[1] ===
                            Math.round(this.props.colorBar.range[1])) ||
                        this.state.bmodeColorBarRange[0] ===
                          this.state.bmodeColorBarRange[1]
                      }
                      className={classes.buttonSecondary}
                      variant="contained"
                      color="secondary"
                    >
                      Apply
                    </Button>
                    <Button
                      onClick={this.commitGlobalBmodeColorBarChange}
                      disabled={
                        this.props.switchDisabled ||
                        !this.props.samplesDoneFlag ||
                        this.state.bmodeColorBarRange[0] ===
                          this.state.bmodeColorBarRange[1]
                      }
                      className={classes.buttonSecondary}
                      variant="contained"
                      color="secondary"
                    >
                      Apply to all images
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state) => {
  const sampleId = state.samples.selectedSampleId;
  const locationId = state.locations.selectedLocationId;
  const sample = state.samples.byId[sampleId];

  //Graph title
  const locationSamples = state.locations.byId[locationId].samples;
  let locationBMODESamples = [];
  for (let locationSampleId of locationSamples) {
    if (state.samples.byId[locationSampleId].type === "BMODE") {
      locationBMODESamples.push(locationSampleId);
    }
  }

  let switchDisabled =
    state.studyInfo.id === studyFixture.study.studyInfo.id ? true : false;

  // state.device.acquiring ||
  // sample.processing ||
  // state.studyInfo.locked ||
  // !sample.dataAvailable;
  return {
    acqTypes: state.protocol.acq_types,
    sample: sample,
    locked: state.studyInfo.locked,
    locationId: locationId,
    roiReference: state.locations.byId[locationId].roiReference,
    autoTopSurface: sample.autoDetectedTopSurface,
    autoBottomSurface: sample.autoDetectedBottomSurface,
    backgroundImage: sample.image,
    xAxis: sample.xAxis,
    yAxis: sample.yAxis,
    colorBar: sample.colorBar,
    topSurface: sample.topSurface,
    bottomSurface: sample.bottomSurface,
    sampleCount: locationBMODESamples.indexOf(sampleId) + 1,
    totalSamples: locationBMODESamples.length,
    switchDisabled: switchDisabled,

    samplesDoneFlag: state.utility.samplesDone,

    setToAutoDetectedThickness: sample.setToAutoDetectedThickness,
    theme: state.utility.theme,
  };
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      updateLocationWideRoiReference,
      setBmodeThickness,
      calculateThickness,
      updateDefaultRoiReference,
      setDefaultRoiReference,
      setLocationWideRoiReference,
      fetchBmodePlot,
      changeGlobalBmodeColorbar,
      toggleBmodeSetToAutoDetectedThickness,
    },
    dispatch
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(styles)(BmodeHeatmap));
